/**
 * SidebarMetadataSection — OrgPilot local copy.
 * Identical to organisms/chat/sidebar/SidebarMetadataSection but with local imports.
 */
import { Button } from "@ncoder/ui";
import { cn } from "@/lib/utils";
import { PlayCircleIcon, FileText } from "lucide-react";
import { ChatTypeDropdown } from "./ChatTypeDropdown";
import { QuickOpen } from "@/components/organisms/metadata_v1";
import { SidebarSearchBar } from "@/components/organisms/metadata/SidebarSearchBar";
import type { MetadataItem } from "@/interfaces/metadata/metadata";

type MetadataTypeOption = { id: string; name: string; value: string; count: number };

interface SidebarMetadataSectionProps {
  metadataTypeOptions: MetadataTypeOption[];
  selectedMetadata: { id: string; name: string } | null;
  setSelectedMetadata: (v: { id: string; name: string } | null) => void;
  metadataFiles: MetadataItem[];
  loadingFiles: boolean;
  loadingMoreFiles: boolean;
  metadataSearchTerm: string;
  hasNextPage: boolean;
  isQuickOpenOpen: boolean;
  setIsQuickOpenOpen: (v: boolean) => void;
  onLoadMore: () => void;
  /** Single click — opens a preview tab when the surface supports previews. */
  onOpenFileInEditor?: (file: MetadataItem) => void;
  /**
   * Double click — open a tab that the next single click will not replace. Omit on
   * surfaces where every open is already permanent.
   */
  onOpenFilePersistent?: (file: MetadataItem) => void;
  /**
   * The editor's active file id. The matching row is highlighted, so the sidebar
   * says which file you are looking at — the same feedback the Metadata Explorer
   * tree gives, using the same design tokens.
   */
  activeFileId?: string | null;
  onExecuteFile?: (file: MetadataItem) => void;
  /** When true, all file selections are disabled (agent is streaming) */
  disabled?: boolean;
}

export function SidebarMetadataSection({
  metadataTypeOptions,
  selectedMetadata,
  setSelectedMetadata,
  metadataFiles,
  loadingFiles,
  loadingMoreFiles,
  metadataSearchTerm,
  hasNextPage,
  isQuickOpenOpen,
  setIsQuickOpenOpen,
  onLoadMore,
  onOpenFileInEditor,
  onOpenFilePersistent,
  onExecuteFile,
  activeFileId,
  disabled,
}: SidebarMetadataSectionProps) {
  const filteredFiles = metadataFiles.filter((file) =>
    file.name.toLowerCase().includes(metadataSearchTerm.toLowerCase())
  );

  return (
    <div className="p-4 border-b gap-4 flex flex-col">
      <div className="flex flex-col gap-4">
        <span className="font-semibold text-sm">MetaData</span>
        <SidebarSearchBar
          searchTerm=""
          onClick={() => setIsQuickOpenOpen(true)}
          readOnly={true}
        />
      </div>
      <QuickOpen
        files={[]}
        onFileSelect={(file) => {
          if (file.metadataType) {
            const opt = metadataTypeOptions.find((o) => o.id === file.metadataType);
            if (opt) setSelectedMetadata({ id: opt.id, name: opt.name });
          }
          if (onOpenFileInEditor) {
            onOpenFileInEditor({
              id: file.id,
              name: file.name,
              fullName: file.path || file.name,
              metadataType: file.metadataType,
              status: "active",
              type: file.type === "file" ? "file" : undefined,
            });
          }
          setIsQuickOpenOpen(false);
        }}
        isOpen={isQuickOpenOpen}
        onOpenChange={setIsQuickOpenOpen}
      />
      <div className="flex flex-row justify-between">
        <ChatTypeDropdown
          options={metadataTypeOptions}
          setValue={setSelectedMetadata}
          value={selectedMetadata}
          className="w-full"
        />
      </div>

      {selectedMetadata && (
        <div className="mt-2 space-y-1">
          <div className="max-h-64 min-h-[5rem] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50">
            {loadingFiles ? (
              <div className="flex items-center justify-center py-4">
                <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredFiles.length > 0 ? (
              <>
                <div className="text-xs font-medium text-muted-foreground mb-2">
                  Files ({filteredFiles.length}
                  {metadataSearchTerm && ` of ${metadataFiles.length}`})
                </div>
                {filteredFiles.map((file) => {
                  // Ids are compared as strings: the editor stores tab ids as
                  // strings while a MetadataItem id can arrive as a number.
                  const isActive =
                    activeFileId != null && String(file.id) === String(activeFileId);
                  return (
                  <div
                    key={file.id}
                    aria-current={isActive ? "true" : undefined}
                    className={cn(
                      "flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors group",
                      // Same tokens as the Metadata Explorer's tree rows, including
                      // the left marker, so the two sidebars read identically.
                      "border-l-2 border-l-transparent",
                      isActive
                        ? "bg-file-explorer-selected text-file-explorer-selected-foreground border-l-file-explorer-selected-border"
                        : "hover:bg-file-explorer-hover hover:text-file-explorer-hover-foreground",
                    )}
                  >
                    <FileText
                      className={cn(
                        "h-3.5 w-3.5 flex-shrink-0",
                        isActive ? "text-foreground" : "text-muted-foreground",
                      )}
                    />
                    <span
                      className={cn(
                        "truncate flex-1",
                        disabled
                          ? "opacity-40 cursor-not-allowed"
                          : "cursor-pointer hover:text-primary",
                        isActive && "font-medium",
                      )}
                      onClick={() => {
                        if (disabled) return;
                        if (file.metadataType && onOpenFileInEditor) onOpenFileInEditor(file);
                        else if (!file.metadataType && onExecuteFile) onExecuteFile(file);
                      }}
                      onDoubleClick={() => {
                        if (disabled || !file.metadataType) return;
                        // The browser fires click before dblclick, so the preview
                        // is already open and loading; this only promotes it.
                        onOpenFilePersistent?.(file);
                      }}
                      title={
                        file.metadataType
                          ? onOpenFilePersistent
                            ? "Click to preview, double-click to keep the tab open"
                            : "Click to open in editor and add to chat"
                          : "Click to add to chat"
                      }
                    >
                      {file.name}
                    </span>
                    {onExecuteFile && !file.metadataType && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onExecuteFile(file);
                        }}
                        className="opacity-0 group-hover:opacity-100 flex-shrink-0 h-6 w-6 flex items-center justify-center rounded hover:bg-primary/10 text-primary transition-opacity"
                        title="Add file to chat input"
                      >
                        <PlayCircleIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  );
                })}
              </>
            ) : (
              <div className="text-xs text-muted-foreground py-2 text-center">
                {metadataSearchTerm ? "No files match your search" : "No files found"}
              </div>
            )}
          </div>
          {!loadingFiles && hasNextPage && !metadataSearchTerm && (
            <div className="border-t">
              <Button
                variant="link"
                size="sm"
                onClick={onLoadMore}
                disabled={loadingMoreFiles}
                className="w-full text-xs"
              >
                {loadingMoreFiles ? (
                  <>
                    <div className="h-3 w-3 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                    Loading...
                  </>
                ) : (
                  `Load more (${metadataFiles.length} of ${
                    metadataTypeOptions.find((o) => o.id === selectedMetadata.id)?.count ??
                    metadataFiles.length
                  })`
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
